import { createFileRoute } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import * as z from 'zod'
import { useAppForm } from '@/lib/use-app-form'
import { PasswordAuthLayout } from './-components/shared/password-auth-layout'
import { AuthPending } from './-components/app-layout-skeleton'
import { useMutation } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { trpc } from '@/main'

export const Route = createFileRoute('/_app/_public/update-password/$token')({
  component: RouteComponent,
  pendingComponent: AuthPending,
})

function RouteComponent() {
  const { token } = Route.useParams()
  const { t } = useLingui()
  const navigate = useNavigate()
  const [email, setEmail] = useState<string>()
  // A token that resolves to nothing live (checkPasswordToken throws TOKEN_INVALID) must not show a
  // usable form — there's nothing to submit it against. Track that and render an error instead, so a
  // bogus/expired link can't surface the password field at all.
  const [invalid, setInvalid] = useState(false)
  const { mutateAsync: checkToken } = useMutation(trpc.auth.checkPasswordToken.mutationOptions())
  const { mutateAsync: updatePassword } = useMutation(trpc.auth.updatePassword.mutationOptions())

  useEffect(() => {
    checkToken(token)
      .then((res) => setEmail(res.email ?? undefined))
      .catch(() => setInvalid(true))
  }, [token, checkToken])

  const schema = z.object({ password: z.string().min(1) })

  const form = useAppForm({
    validators: { onChange: schema },
    defaultValues: { password: '' },
    onSubmit: async ({ value }) => {
      await updatePassword({ token, password: value.password })
      toast.success(t`Password updated successfully`)
      navigate({ to: '/login' })
    },
  })

  return (
    <PasswordAuthLayout title={t`Enter your new password`} subtitle={t`Choose a new password for your account.`}>
      {invalid ? (
        <p className="text-muted-foreground text-sm">{t`This link is invalid or has expired`}</p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.handleSubmit()
          }}
          className="flex flex-col gap-3"
        >
          {/* Username field so the browser associates the new password with this account. */}
          {email && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">{t`Email`}</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                readOnly
                className="border-input bg-muted/50 text-muted-foreground h-8 cursor-not-allowed rounded-lg border px-2.5 py-1 text-sm"
              />
            </div>
          )}
          <form.AppField
            name="password"
            children={(field) => <field.PasswordField label={t`Password`} autoComplete="new-password" />}
          />

          <form.AppForm>
            <form.SubmitButton label={t`Save`} />
          </form.AppForm>
        </form>
      )}
    </PasswordAuthLayout>
  )
}
